<template>
  <div 
    @click="handleClick"
    @auxclick="handleAuxClick"
    @contextmenu="handleRightClick"
    class="grid cursor-pointer grid-cols-12 items-center gap-4 px-4 py-3.5 transition-colors"
    :class="[
      isSelected ? 'bg-blue-50 shadow-[inset_3px_0_0_0_#0ea5e9]' : 'hover:bg-slate-50',
      !isLast ? 'border-b border-slate-100' : ''
    ]"
  >
    <!-- Player ID -->
    <div class="col-span-2">
      <span class="text-sm font-semibold text-rose-600">#{{ player.player_id || player.id }}</span>
    </div>

    <!-- Player Name -->
    <div class="col-span-4">
      <div class="flex flex-col">
        <span class="text-sm font-medium text-slate-900">{{ player.player_name || 'Unknown Player' }}</span>
        <span v-if="player.matched_name && player.matched_name !== player.player_name" class="text-xs text-slate-400">
          Matched: {{ player.matched_name }}
        </span>
      </div>
    </div>

    <!-- Position -->
    <div class="col-span-2">
      <span class="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600">{{ player.position || '-' }}</span>
    </div>

    <!-- Team -->
    <div class="col-span-2">
      <span class="text-sm text-slate-500">{{ player.team?.team_name || player.team_name || '-' }}</span>
    </div>

    <!-- Actions -->
    <div class="col-span-2 flex items-center justify-end gap-1.5">
      <!-- View Button -->
      <button
        @click.stop="handleClick"
        @auxclick.stop="handleAuxClick"
        class="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-slate-700"
        title="Click to open player profile, Ctrl+Click for new tab"
      >
        View
      </button>
      
      <!-- New Tab Button -->
      <button
        @click.stop="openInNewTab"
        class="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        title="Open in new tab"
      >
        <i class="fas fa-external-link-alt text-sm"></i>
      </button>
    </div>

    <!-- Context Menu -->
    <div 
      v-if="showContextMenu"
      @click.stop
      class="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50"
      :style="{ left: contextMenuX + 'px', top: contextMenuY + 'px' }"
    >
      <button
        @click="openInCurrentTab"
        class="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
      >
        <i class="fas fa-eye text-blue-500"></i>
        Open player profile
      </button>
      <button
        @click="openInNewTab"
        class="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
      >
        <i class="fas fa-external-link-alt text-blue-500"></i>
        Open in new tab
      </button>
      <hr class="my-1">
      <button
        @click="copyLink"
        class="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
      >
        <i class="fas fa-copy text-gray-500"></i>
        Copy link
      </button>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  player: {
    type: Object,
    required: true
  },
  isSelected: {
    type: Boolean,
    default: false
  },
  isLast: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['select'])

const showContextMenu = ref(false)
const contextMenuX = ref(0)
const contextMenuY = ref(0)

const handleClick = (event) => {
  if (event.ctrlKey || event.metaKey) {
    // Ctrl+Click or Cmd+Click - open in new tab
    openInNewTab()
  } else {
    // Regular click - open in current tab
    openInCurrentTab()
  }
}

const handleAuxClick = (event) => {
  // Middle mouse button click - open in new tab
  if (event.button === 1) {
    event.preventDefault()
    openInNewTab()
  }
}

const handleRightClick = (event) => {
  event.preventDefault()
  contextMenuX.value = event.clientX
  contextMenuY.value = event.clientY
  showContextMenu.value = true
}

const openInCurrentTab = () => {
  hideContextMenu()
  emit('select', props.player)
}

const openInNewTab = () => {
  hideContextMenu()
  const playerId = props.player.player_id || props.player.id
  window.open(`/player/${playerId}`, '_blank', 'noopener,noreferrer')
}

const copyLink = async () => {
  hideContextMenu()
  const playerId = props.player.player_id || props.player.id
  const url = `${window.location.origin}/player/${playerId}`
  try {
    await navigator.clipboard.writeText(url)
    console.log('Link copied to clipboard')
  } catch (err) {
    console.error('Failed to copy link:', err)
  }
}

const hideContextMenu = () => {
  showContextMenu.value = false
}

const handleClickOutside = (event) => {
  if (showContextMenu.value) {
    hideContextMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
